/**
 * Custom element <geolocation-info-panel> for map marker info panels.
 * Call open(title, body, url?) to populate and show the panel.
 * The close button dispatches an `info-panel-close` bubbling event.
 */
const OPEN_CLASS = 'is-open';

export default class GeolocationInfoPanel extends HTMLElement {
    private titleEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private linkEl: HTMLAnchorElement | null = null;
    private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

    connectedCallback(): void {
        const titleSelector = this.getAttribute('data-info-panel-title-selector') ?? '[data-info-panel-title]';
        const bodySelector = this.getAttribute('data-info-panel-body-selector') ?? '[data-info-panel-body]';
        const linkSelector = this.getAttribute('data-info-panel-link-selector') ?? '[data-info-panel-link] a';
        const closeSelector = this.getAttribute('data-info-panel-close-selector') ?? '[data-info-panel-close]';

        this.titleEl = this.querySelector(titleSelector);
        this.bodyEl = this.querySelector(bodySelector);
        this.linkEl = this.querySelector(linkSelector);

        this.querySelector(closeSelector)
            ?.addEventListener('click', () => this.close());

        this.outsideClickHandler = (e: MouseEvent) => {
            if (!this.contains(e.target as Node)) {
                this.close();
            }
        };
    }

    disconnectedCallback(): void {
        this.removeOutsideListener();
    }

    private addOutsideListener(): void {
        if (this.outsideClickHandler) {
            document.addEventListener('click', this.outsideClickHandler);
        }
    }

    private removeOutsideListener(): void {
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
        }
    }

    open(title: string, body: string, url?: string): void {
        if (this.titleEl) this.titleEl.textContent = title;
        if (this.bodyEl) this.bodyEl.innerHTML = body;
        if (this.linkEl) {
            if (url) {
                this.linkEl.href = url;
                this.linkEl.style.display = '';
            } else {
                this.linkEl.style.display = 'none';
            }
        }
        this.classList.add(OPEN_CLASS);
        setTimeout(() => this.addOutsideListener(), 0);
    }

    close(): void {
        this.removeOutsideListener();
        this.classList.remove(OPEN_CLASS);
        this.dispatchEvent(new CustomEvent('info-panel-close', { bubbles: true }));
    }

    public static defineElement(): void {
        if (!window.customElements.get('geolocation-info-panel')) {
            window.customElements.define('geolocation-info-panel', GeolocationInfoPanel);
        }
    }
}
